
import { AIModule } from '../types';

export const ENHANCED_AI_FUNDAMENTALS_MODULE_1: AIModule = {
  id: 'ai-fund-1',
  title: 'Introduction to AI',
  description: 'Comprehensive introduction to AI concepts, history, and applications',
  content: `
# Introduction to Artificial Intelligence

## Learning Objectives
By the end of this module, you will be able to:
- Define artificial intelligence and understand its core concepts
- Identify different types of AI and their applications
- Recognize AI in everyday life and business applications
- Understand the ethical considerations in AI development

## Prerequisites
- Basic understanding of mathematics and logic
- Familiarity with programming concepts (helpful but not required)
- Critical thinking skills

## Module Overview
Artificial Intelligence (AI) is transforming every aspect of our lives, from the way we work to how we interact with technology. This module provides a comprehensive introduction to AI, covering its history, current applications, and future potential.

**Estimated Time:** 90 minutes
**Difficulty Level:** beginner

---

## Key Concepts

### What is Artificial Intelligence?
Artificial Intelligence refers to the simulation of human intelligence in machines that are programmed to think and learn like humans.

#### Why This Matters
AI is revolutionizing industries, creating new job opportunities, and solving complex problems that were previously impossible to address. Understanding AI is crucial for navigating our increasingly digital world.

#### Real-World Application
AI powers recommendation systems on Netflix and Amazon, enables voice assistants like Siri and Alexa, and drives autonomous vehicles. In healthcare, AI helps diagnose diseases and discover new drugs.

### Types of Artificial Intelligence
AI can be categorized into different types based on capabilities and functionality.

#### Best Practices
- **Narrow AI (Weak AI)**: Designed for specific tasks (e.g., image recognition, chess playing)
- **General AI (Strong AI)**: Hypothetical AI with human-level cognitive abilities
- **Artificial Superintelligence**: AI that surpasses human intelligence (theoretical)

### Machine Learning vs. AI
Machine Learning is a subset of AI that focuses on algorithms that can learn from data.

---

## Hands-On Examples

### Example 1: AI in Image Recognition
Understanding how AI systems can identify objects in images.

\`\`\`python
# Simple image classification concept
import numpy as np

def simple_image_classifier(image_features):
    """
    Simplified representation of how AI classifies images
    """
    # In reality, this would use complex neural networks
    if image_features['has_fur'] and image_features['has_ears']:
        return 'cat'
    elif image_features['has_wheels'] and image_features['has_windows']:
        return 'car'
    else:
        return 'unknown'

# Example usage
image_data = {'has_fur': True, 'has_ears': True, 'has_wheels': False}
result = simple_image_classifier(image_data)
print(f"Classified as: {result}")
\`\`\`

**Explanation:** This simplified example shows the basic concept of how AI systems make decisions based on features. Real AI systems use much more sophisticated algorithms and neural networks.

### Example 2: Basic Decision Tree
Understanding how AI makes decisions using tree-like structures.

\`\`\`python
# Simple decision tree for weather recommendation
def weather_activity_recommender(weather_conditions):
    """
    AI-like decision making for activity recommendation
    """
    if weather_conditions['temperature'] > 75:
        if weather_conditions['sunny']:
            return 'Go to the beach'
        else:
            return 'Have a picnic in the park'
    else:
        if weather_conditions['raining']:
            return 'Stay inside and read'
        else:
            return 'Go for a walk'

# Example usage
today_weather = {'temperature': 80, 'sunny': True, 'raining': False}
recommendation = weather_activity_recommender(today_weather)
print(f"AI recommends: {recommendation}")
\`\`\`

**Key Points:**
- Decision trees are fundamental building blocks of many AI systems
- AI systems process multiple inputs to make informed decisions

---

## Industry Applications

### How Professionals Use This
AI professionals work across various industries:

- **Technology Companies**: Develop AI products and services like search engines and recommendation systems
- **Healthcare**: Create diagnostic tools and drug discovery platforms
- **Finance**: Build fraud detection systems and algorithmic trading platforms
- **Automotive**: Design autonomous vehicle systems and driver assistance features

### Popular Tools and Technologies
- **Programming Languages**: Python, R, JavaScript for AI development
- **Frameworks**: TensorFlow, PyTorch, scikit-learn for machine learning
- **Cloud Platforms**: AWS, Google Cloud, Azure for AI deployment

### Career Relevance
AI skills are increasingly valuable across all industries. Understanding AI fundamentals opens doors to roles in data science, machine learning engineering, and AI product management.

---

## Key Takeaways

✅ **Essential Concepts:**
- AI simulates human intelligence in machines
- Machine learning is a subset of AI focused on learning from data
- AI applications are everywhere in modern life

⚠️ **Common Pitfalls to Avoid:**
- Expecting AI to solve all problems immediately
- Underestimating the importance of data quality in AI systems

🚀 **Next Steps:**
- Explore specific AI applications in your field of interest
- Learn basic programming concepts to better understand AI implementation

## Prepare for Practice
Apply these AI concepts in the Practice tab through hands-on exercises with real AI tools and datasets.

---
`,
  duration: '90 minutes',
  difficulty: 'beginner',
  order: 1,
  estimatedTime: '90 minutes',
  objectives: [
    'Define artificial intelligence and understand its core concepts',
    'Identify different types of AI and their applications',
    'Recognize AI in everyday life and business applications',
    'Understand the ethical considerations in AI development'
  ],
  learningObjectives: [
    'Define artificial intelligence and understand its core concepts',
    'Identify different types of AI and their applications',
    'Recognize AI in everyday life and business applications',
    'Understand the ethical considerations in AI development'
  ],
  prerequisites: ['Basic mathematics and logic', 'Programming concepts (helpful)'],
  codeExamples: [
    {
      id: 'ai-image-classifier',
      title: 'Simple Image Classifier',
      description: 'Basic concept of how AI classifies images based on features',
      code: `def simple_image_classifier(image_features):
    if image_features['has_fur'] and image_features['has_ears']:
        return 'cat'
    elif image_features['has_wheels'] and image_features['has_windows']:
        return 'car'
    else:
        return 'unknown'`,
      language: 'python',
      category: 'demo',
      libraries: ['numpy']
    },
    {
      id: 'ai-decision-tree',
      title: 'Weather Activity Recommender',
      description: 'AI-like decision making using tree structures',
      code: `def weather_activity_recommender(weather_conditions):
    if weather_conditions['temperature'] > 75:
        if weather_conditions['sunny']:
            return 'Go to the beach'
        else:
            return 'Have a picnic in the park'
    else:
        if weather_conditions['raining']:
            return 'Stay inside and read'
        else:
            return 'Go for a walk'`,
      language: 'python',
      category: 'tutorial',
      libraries: []
    }
  ],
  practice: {
    task: `
## Your Challenge
Build a simple AI-powered recommendation system that suggests movies based on user preferences and viewing history.

### Requirements
- Create a basic recommendation algorithm
- Handle different user preference categories
- Implement a rating prediction system
- Test with sample user data

### Success Criteria
- System provides relevant movie recommendations
- Algorithm considers multiple factors (genre, rating, year)
- Code is well-documented and easy to understand
    `,
    starter: `# AI Movie Recommendation System

# Step 1: Define movie data structure
movies = [
    {"id": 1, "title": "The Matrix", "genre": "sci-fi", "rating": 8.7, "year": 1999},
    {"id": 2, "title": "Inception", "genre": "sci-fi", "rating": 8.8, "year": 2010},
    {"id": 3, "title": "The Godfather", "genre": "drama", "rating": 9.2, "year": 1972}
]

# Step 2: Define user preferences
user_preferences = {
    "favorite_genres": ["sci-fi", "action"],
    "min_rating": 8.0,
    "max_year": 2020
}

# Step 3: Create recommendation function
def recommend_movies(user_prefs, movie_list):
    # Movie Recommendation System
    
    import pandas as pd
    import numpy as np
    from sklearn.feature_extraction.text import TfidfVectorizer
    from sklearn.metrics.pairwise import cosine_similarity
    from sklearn.decomposition import TruncatedSVD
    
    class MovieRecommendationSystem:
        def __init__(self):
            self.tfidf = TfidfVectorizer(max_features=5000, stop_words='english')
            self.svd = TruncatedSVD(n_components=100, random_state=42)
            self.movies_df = None
            self.content_matrix = None
            self.ratings_matrix = None
            
        def load_movie_data(self, movies_file, ratings_file):
            """Load and preprocess movie and ratings data"""
            self.movies_df = pd.read_csv(movies_file)
            ratings_df = pd.read_csv(ratings_file)
            
            # Create content-based features from movie descriptions
            movie_features = self.movies_df['genres'] + ' ' + self.movies_df.get('overview', '')
            self.content_matrix = self.tfidf.fit_transform(movie_features)
            
            # Create user-item matrix for collaborative filtering
            self.ratings_matrix = ratings_df.pivot_table(
                index='userId', columns='movieId', values='rating'
            ).fillna(0)
            
            # Apply SVD for dimensionality reduction
            self.ratings_reduced = self.svd.fit_transform(self.ratings_matrix)
            
        def content_based_recommend(self, movie_id, num_recommendations=5):
            """Recommend movies based on content similarity"""
            if movie_id not in self.movies_df['movieId'].values:
                return []
                
            movie_idx = self.movies_df[self.movies_df['movieId'] == movie_id].index[0]
            
            # Calculate cosine similarity
            similarity_scores = cosine_similarity(
                self.content_matrix[movie_idx:movie_idx+1], 
                self.content_matrix
            ).flatten()
            
            # Get top similar movies
            similar_indices = similarity_scores.argsort()[-num_recommendations-1:-1][::-1]
            
            recommendations = []
            for idx in similar_indices:
                if idx != movie_idx:
                    recommendations.append({
                        'movieId': self.movies_df.iloc[idx]['movieId'],
                        'title': self.movies_df.iloc[idx]['title'],
                        'similarity_score': similarity_scores[idx]
                    })
                    
            return recommendations
            
        def collaborative_filtering_recommend(self, user_id, num_recommendations=5):
            """Recommend movies based on user similarity"""
            if user_id not in self.ratings_matrix.index:
                return self.popular_movies_recommend(num_recommendations)
                
            user_idx = list(self.ratings_matrix.index).index(user_id)
            user_ratings = self.ratings_reduced[user_idx].reshape(1, -1)
            
            # Find similar users
            user_similarities = cosine_similarity(user_ratings, self.ratings_reduced).flatten()
            similar_user_indices = user_similarities.argsort()[-10:][::-1]
            
            # Get movies highly rated by similar users
            movie_scores = {}
            for similar_user_idx in similar_user_indices:
                similar_user_id = self.ratings_matrix.index[similar_user_idx]
                user_movies = self.ratings_matrix.loc[similar_user_id]
                
                for movie_id, rating in user_movies.items():
                    if rating > 4.0 and self.ratings_matrix.loc[user_id, movie_id] == 0:
                        if movie_id not in movie_scores:
                            movie_scores[movie_id] = []
                        movie_scores[movie_id].append(rating * user_similarities[similar_user_idx])
            
            # Sort by average weighted score
            recommendations = []
            for movie_id, scores in sorted(
                movie_scores.items(), 
                key=lambda x: np.mean(x[1]), 
                reverse=True
            )[:num_recommendations]:
                movie_info = self.movies_df[self.movies_df['movieId'] == movie_id]
                if not movie_info.empty:
                    recommendations.append({
                        'movieId': movie_id,
                        'title': movie_info.iloc[0]['title'],
                        'predicted_rating': np.mean(scores)
                    })
                    
            return recommendations
            
        def hybrid_recommend(self, user_id, movie_id=None, num_recommendations=5):
            """Combine content-based and collaborative filtering"""
            content_recs = []
            collab_recs = []
            
            if movie_id:
                content_recs = self.content_based_recommend(movie_id, num_recommendations)
            
            collab_recs = self.collaborative_filtering_recommend(user_id, num_recommendations)
            
            # Merge and weight recommendations
            all_recs = {}
            
            # Weight content-based recommendations (40%)
            for rec in content_recs:
                movie_id = rec['movieId']
                all_recs[movie_id] = {
                    'title': rec['title'],
                    'score': rec['similarity_score'] * 0.4,
                    'type': 'content'
                }
            
            # Weight collaborative recommendations (60%)
            for rec in collab_recs:
                movie_id = rec['movieId']
                if movie_id in all_recs:
                    all_recs[movie_id]['score'] += rec['predicted_rating'] * 0.6
                    all_recs[movie_id]['type'] = 'hybrid'
                else:
                    all_recs[movie_id] = {
                        'title': rec['title'],
                        'score': rec['predicted_rating'] * 0.6,
                        'type': 'collaborative'
                    }
            
            # Sort by final score
            final_recs = sorted(
                all_recs.items(), 
                key=lambda x: x[1]['score'], 
                reverse=True
            )[:num_recommendations]
            
            return [
                {
                    'movieId': movie_id,
                    'title': data['title'],
                    'score': data['score'],
                    'recommendation_type': data['type']
                }
                for movie_id, data in final_recs
            ]
    
    # Example usage
    recommender = MovieRecommendationSystem()
    # recommender.load_movie_data('movies.csv', 'ratings.csv')
    # recommendations = recommender.hybrid_recommend(user_id=123, movie_id=456)
    pass

# Step 4: Test the system
recommendations = recommend_movies(user_preferences, movies)
print("Recommended movies:", recommendations)`,
    solution: `# Complete AI Movie Recommendation System

import math

movies = [
    {"id": 1, "title": "The Matrix", "genre": "sci-fi", "rating": 8.7, "year": 1999},
    {"id": 2, "title": "Inception", "genre": "sci-fi", "rating": 8.8, "year": 2010},
    {"id": 3, "title": "The Godfather", "genre": "drama", "rating": 9.2, "year": 1972},
    {"id": 4, "title": "Interstellar", "genre": "sci-fi", "rating": 8.6, "year": 2014},
    {"id": 5, "title": "Pulp Fiction", "genre": "crime", "rating": 8.9, "year": 1994}
]

user_preferences = {
    "favorite_genres": ["sci-fi", "action"],
    "min_rating": 8.0,
    "max_year": 2020,
    "watched_movies": [1, 3]  # Movies already watched
}

def calculate_movie_score(movie, user_prefs):
    """
    Calculate a recommendation score for a movie based on user preferences
    """
    score = 0
    
    # Genre preference (40% weight)
    if movie["genre"] in user_prefs["favorite_genres"]:
        score += 4
    
    # Rating factor (30% weight)
    if movie["rating"] >= user_prefs["min_rating"]:
        score += 3 * (movie["rating"] / 10)
    
    # Recency factor (20% weight)
    current_year = 2024
    years_old = current_year - movie["year"]
    recency_score = max(0, 2 - (years_old / 10))
    score += recency_score
    
    # Year filter (10% weight)
    if movie["year"] <= user_prefs["max_year"]:
        score += 1
    
    return round(score, 2)

def recommend_movies(user_prefs, movie_list, num_recommendations=3):
    """
    AI-powered movie recommendation system
    """
    # Filter out already watched movies
    unwatched_movies = [
        movie for movie in movie_list 
        if movie["id"] not in user_prefs.get("watched_movies", [])
    ]
    
    # Calculate scores for each movie
    movie_scores = []
    for movie in unwatched_movies:
        score = calculate_movie_score(movie, user_prefs)
        movie_scores.append({
            "movie": movie,
            "score": score
        })
    
    # Sort by score (highest first)
    movie_scores.sort(key=lambda x: x["score"], reverse=True)
    
    # Return top recommendations
    recommendations = []
    for item in movie_scores[:num_recommendations]:
        recommendations.append({
            "title": item["movie"]["title"],
            "genre": item["movie"]["genre"],
            "rating": item["movie"]["rating"],
            "year": item["movie"]["year"],
            "recommendation_score": item["score"],
            "reason": generate_recommendation_reason(item["movie"], user_prefs)
        })
    
    return recommendations

def generate_recommendation_reason(movie, user_prefs):
    """
    Generate human-readable explanation for recommendation
    """
    reasons = []
    
    if movie["genre"] in user_prefs["favorite_genres"]:
        reasons.append(f"matches your favorite genre ({movie['genre']})")
    
    if movie["rating"] >= user_prefs["min_rating"]:
        reasons.append(f"high rating ({movie['rating']}/10)")
    
    if movie["year"] >= 2010:
        reasons.append("recent release")
    
    return ", ".join(reasons)

# Test the AI recommendation system
recommendations = recommend_movies(user_preferences, movies)

print("🎬 AI Movie Recommendations for You:")
print("=" * 50)

for i, rec in enumerate(recommendations, 1):
    print(f"{i}. {rec['title']} ({rec['year']})")
    print(f"   Genre: {rec['genre']} | Rating: {rec['rating']}/10")
    print(f"   AI Score: {rec['recommendation_score']}/10")
    print(f"   Why: {rec['reason']}")
    print()

# Advanced: User feedback learning simulation
def update_preferences_based_on_feedback(user_prefs, movie, liked):
    """
    Simulate AI learning from user feedback
    """
    if liked:
        # Add genre to favorites if not already there
        if movie["genre"] not in user_prefs["favorite_genres"]:
            user_prefs["favorite_genres"].append(movie["genre"])
        
        # Lower minimum rating threshold slightly
        user_prefs["min_rating"] = max(7.0, user_prefs["min_rating"] - 0.1)
    else:
        # Remove genre from favorites if present
        if movie["genre"] in user_prefs["favorite_genres"]:
            user_prefs["favorite_genres"].remove(movie["genre"])
        
        # Raise minimum rating threshold
        user_prefs["min_rating"] = min(9.0, user_prefs["min_rating"] + 0.1)
    
    return user_prefs

print("🤖 AI Learning Simulation:")
print("User liked Inception - updating preferences...")
updated_prefs = update_preferences_based_on_feedback(
    user_preferences.copy(), 
    {"genre": "sci-fi", "rating": 8.8}, 
    liked=True
)
print(f"Updated preferences: {updated_prefs}")`,
    hints: [
      'Start with a simple scoring system based on genre matching',
      'Consider multiple factors: genre, rating, year, user history',
      'Use weighted scores to balance different preference factors',
      'Think about how real AI systems learn from user feedback'
    ],
    estimatedTime: 60
  },
  quiz: {
    questions: [
      {
        id: 'ai-fundamentals-q1',
        question: 'What is the main difference between Narrow AI and General AI?',
        options: [
          'Narrow AI is slower than General AI',
          'Narrow AI is designed for specific tasks, while General AI has human-level cognitive abilities',
          'Narrow AI uses more computational power',
          'There is no difference between them'
        ],
        correctAnswer: 1,
        explanation: 'Narrow AI (Weak AI) is designed to perform specific tasks like image recognition or chess playing, while General AI (Strong AI) would have human-level cognitive abilities across all domains.'
      },
      {
        id: 'ai-fundamentals-q2',
        question: 'Which of the following is NOT a common application of AI in everyday life?',
        options: [
          'Voice assistants like Siri and Alexa',
          'Recommendation systems on Netflix',
          'Traditional calculators',
          'Autonomous vehicle navigation'
        ],
        correctAnswer: 2,
        explanation: 'Traditional calculators perform simple arithmetic operations without any form of artificial intelligence or learning capabilities.'
      },
      {
        id: 'ai-fundamentals-q3',
        question: 'What is Machine Learning in relation to Artificial Intelligence?',
        options: [
          'Machine Learning and AI are completely separate fields',
          'Machine Learning is a subset of AI that focuses on algorithms learning from data',
          'Machine Learning is more advanced than AI',
          'AI is a subset of Machine Learning'
        ],
        correctAnswer: 1,
        explanation: 'Machine Learning is a subset of Artificial Intelligence that specifically focuses on creating algorithms that can learn and improve from data without being explicitly programmed.'
      }
    ]
  }
};

export const ENHANCED_AI_FUNDAMENTALS_MODULE_2: AIModule = {
  id: 'ai-fund-2',
  title: 'Search Algorithms',
  description: 'Introduction to machine learning concepts, algorithms, and applications',
  content: `
# Machine Learning Fundamentals

## Learning Objectives
By the end of this module, you will be able to:
- Understand the core concepts of machine learning
- Differentiate between supervised, unsupervised, and reinforcement learning
- Identify common machine learning algorithms and their use cases
- Recognize the importance of data in machine learning

## Prerequisites
- Basic understanding of AI concepts (Module 1)
- Elementary statistics and mathematics
- Basic programming knowledge (Python recommended)

## Module Overview
Machine Learning (ML) is the engine that powers modern AI applications. This module introduces you to the fundamental concepts, types, and algorithms that make machines capable of learning from data.

**Estimated Time:** 120 minutes
**Difficulty Level:** intermediate

---

## Key Concepts

### What is Machine Learning?
Machine Learning is a method of data analysis that automates analytical model building using algorithms that iteratively learn from data.

#### Why This Matters
ML enables computers to find insights in data without being explicitly programmed where to look. This capability is transforming industries and creating new possibilities for automation and intelligence.

#### Real-World Application
ML powers search engines, fraud detection systems, medical diagnosis tools, and recommendation engines that suggest products or content based on user behavior.

### Types of Machine Learning
Understanding the different approaches to machine learning is crucial for selecting the right method for specific problems.

#### Best Practices
- **Supervised Learning**: Learning with labeled examples (classification and regression)
- **Unsupervised Learning**: Finding patterns in data without labels (clustering and association)
- **Reinforcement Learning**: Learning through interaction and feedback (game playing and robotics)

### The Machine Learning Process
A systematic approach to building ML models includes data collection, preprocessing, model selection, training, and evaluation.

---

## Hands-On Examples

### Example 1: Simple Linear Regression
Understanding how machines learn relationships between variables.

\`\`\`python
# Simple linear regression example
import numpy as np
import matplotlib.pyplot as plt

# Sample data: house sizes and prices
house_sizes = np.array([1000, 1200, 1400, 1600, 1800, 2000])
house_prices = np.array([150000, 180000, 210000, 240000, 270000, 300000])

# Calculate slope and intercept (simplified)
def calculate_line_of_best_fit(x, y):
    n = len(x)
    slope = (n * np.sum(x * y) - np.sum(x) * np.sum(y)) / (n * np.sum(x**2) - (np.sum(x))**2)
    intercept = (np.sum(y) - slope * np.sum(x)) / n
    return slope, intercept

slope, intercept = calculate_line_of_best_fit(house_sizes, house_prices)

# Make predictions
new_house_size = 1500
predicted_price = slope * new_house_size + intercept
print(f"Predicted price for 1500 sq ft house: {predicted_price:,.2f}")
\`\`\`

**Explanation:** This example demonstrates how machine learning algorithms find patterns in data by learning mathematical relationships between input features and target values.

### Example 2: Classification with Decision Trees
Understanding how machines make categorical predictions.

\`\`\`python
# Simple classification example
def classify_email(email_features):
    """
    Simple decision tree for email spam classification
    """
    # Check for spam indicators
    spam_score = 0
    
    if email_features.get('has_urgent_words', False):
        spam_score += 2
    
    if email_features.get('num_exclamation_marks', 0) > 3:
        spam_score += 1
    
    if email_features.get('sender_unknown', False):
        spam_score += 2
    
    if email_features.get('has_money_words', False):
        spam_score += 3
    
    # Classification decision
    if spam_score >= 4:
        return 'spam'
    else:
        return 'not spam'

# Example usage
email1 = {
    'has_urgent_words': True,
    'num_exclamation_marks': 5,
    'sender_unknown': True,
    'has_money_words': False
}

result = classify_email(email1)
print(f"Email classified as: {result}")
\`\`\`

**Key Points:**
- Classification algorithms learn to categorize data into predefined classes
- Features are the measurable properties of the data being analyzed

---

## Industry Applications

### How Professionals Use This
ML professionals work on diverse problems:

- **Data Scientists**: Analyze data to extract insights and build predictive models
- **ML Engineers**: Deploy and maintain machine learning systems in production
- **Research Scientists**: Develop new algorithms and techniques
- **Product Managers**: Define ML product requirements and strategy

### Popular Tools and Technologies
- **Python Libraries**: scikit-learn, pandas, numpy for data analysis and ML
- **Deep Learning**: TensorFlow, PyTorch for neural networks
- **Cloud ML**: AWS SageMaker, Google Cloud ML, Azure ML for scalable solutions

### Career Relevance
ML skills are in high demand across industries including technology, finance, healthcare, and retail. Understanding ML fundamentals is essential for modern data-driven careers.

---

## Key Takeaways

✅ **Essential Concepts:**
- Machine learning enables computers to learn patterns from data
- Different types of ML solve different kinds of problems
- Quality data is crucial for successful ML applications

⚠️ **Common Pitfalls to Avoid:**
- Using insufficient or poor-quality training data
- Overfitting models to training data without considering generalization

🚀 **Next Steps:**
- Practice with real datasets using ML libraries
- Learn about feature engineering and data preprocessing

## Prepare for Practice
Apply these ML concepts in the Practice tab by building your own machine learning models.

---
`,
  duration: '120 minutes',
  difficulty: 'intermediate',
  order: 2,
  estimatedTime: '120 minutes',
  objectives: [
    'Understand the core concepts of machine learning',
    'Differentiate between supervised, unsupervised, and reinforcement learning',
    'Identify common machine learning algorithms and their use cases',
    'Recognize the importance of data in machine learning'
  ],
  learningObjectives: [
    'Understand the core concepts of machine learning',
    'Differentiate between supervised, unsupervised, and reinforcement learning',
    'Identify common machine learning algorithms and their use cases',
    'Recognize the importance of data in machine learning'
  ],
  prerequisites: ['AI fundamentals', 'Basic statistics', 'Python programming'],
  practice: {
    task: `
## Your Challenge
Build a machine learning model to predict student performance based on study habits and background factors.

### Requirements
- Implement both classification and regression approaches
- Use multiple features to make predictions
- Evaluate model performance
- Interpret the results

### Success Criteria
- Model achieves reasonable accuracy on test data
- Code demonstrates understanding of ML concepts
- Results are properly interpreted and explained
    `,
    starter: `# Student Performance Prediction System

import numpy as np
import pandas as pd

# Sample student data
students_data = [
    {'study_hours': 5, 'attendance': 85, 'prev_grade': 78, 'final_grade': 82},
    {'study_hours': 3, 'attendance': 70, 'prev_grade': 65, 'final_grade': 68},
    {'study_hours': 8, 'attendance': 95, 'prev_grade': 88, 'final_grade': 91},
    # Add more sample data...
]

# TODO: Implement data preprocessing
# TODO: Create feature matrix and target vector
# TODO: Split data into training and testing sets
# TODO: Train ML model
# TODO: Evaluate performance`,
    hints: [
      'Start by exploring the relationships between different features',
      'Consider both linear and non-linear models',
      'Use cross-validation to assess model performance',
      'Think about which features are most predictive'
    ],
    estimatedTime: 90
  },
  quiz: {
    questions: [
      {
        id: 'ml-fundamentals-q1',
        question: 'What is the main difference between supervised and unsupervised learning?',
        options: [
          'Supervised learning is faster than unsupervised learning',
          'Supervised learning uses labeled data, while unsupervised learning finds patterns in unlabeled data',
          'Supervised learning is more accurate',
          'There is no significant difference'
        ],
        correctAnswer: 1,
        explanation: 'Supervised learning algorithms learn from labeled training data where the correct answers are provided, while unsupervised learning finds hidden patterns in data without labeled examples.'
      },
      {
        id: 'ml-fundamentals-q2',
        question: 'Which of the following is an example of a classification problem?',
        options: [
          'Predicting house prices',
          'Determining email spam vs. not spam',
          'Forecasting stock prices',
          'Estimating temperature'
        ],
        correctAnswer: 1,
        explanation: 'Classification problems involve predicting discrete categories or classes. Email spam detection classifies emails into two categories: spam or not spam.'
      }
    ]
  }
};

export const ENHANCED_AI_FUNDAMENTALS_MODULE_3: AIModule = {
  id: 'ai-fund-3',
  title: 'Machine Learning Basics',
  description: 'Introduction to machine learning concepts, algorithms, and applications',
  content: `
# Machine Learning Fundamentals

Machine Learning is a subset of AI that enables systems to learn and improve from experience without being explicitly programmed.

## What is Machine Learning?

Machine Learning (ML) is the study of computer algorithms that can improve automatically through experience and data. Instead of following explicit instructions, ML systems learn patterns from data.

## Types of Machine Learning

### 1. Supervised Learning
Learning from labeled training data to make predictions.

**Common Algorithms:**
- Linear Regression
- Logistic Regression
- Decision Trees
- Random Forests
- Support Vector Machines (SVM)
- Neural Networks

**Applications:**
- Email spam detection
- Image classification
- Price prediction
- Medical diagnosis

### 2. Unsupervised Learning
Finding hidden patterns in unlabeled data.

**Common Algorithms:**
- K-Means Clustering
- Hierarchical Clustering
- Principal Component Analysis (PCA)
- Anomaly Detection

**Applications:**
- Customer segmentation
- Dimensionality reduction
- Pattern discovery
- Recommendation systems

### 3. Reinforcement Learning
Learning through interaction with environment via rewards and penalties.

**Key Concepts:**
- Agent, Environment, State, Action, Reward
- Policy: Strategy for choosing actions
- Value Function: Expected future reward

**Applications:**
- Game playing (AlphaGo)
- Robotics
- Autonomous vehicles
- Resource management

## The Machine Learning Process

1. **Data Collection**: Gather relevant data
2. **Data Preprocessing**: Clean and prepare data
3. **Feature Engineering**: Select/create meaningful features
4. **Model Selection**: Choose appropriate algorithm
5. **Training**: Learn patterns from data
6. **Evaluation**: Test model performance
7. **Deployment**: Use model in production
8. **Monitoring**: Track performance over time

## Key Concepts

### Training vs Testing Data
- Split data into training and testing sets
- Train on training set, evaluate on test set
- Prevents overfitting

### Overfitting vs Underfitting
- **Overfitting**: Model too complex, memorizes training data
- **Underfitting**: Model too simple, misses patterns
- Goal: Right balance - good generalization

### Feature Engineering
Creating meaningful input variables:
- Feature scaling/normalization
- Feature selection
- Dimensionality reduction
- Creating derived features

### Model Evaluation Metrics

**Classification:**
- Accuracy, Precision, Recall
- F1 Score
- ROC-AUC

**Regression:**
- Mean Squared Error (MSE)
- Root Mean Squared Error (RMSE)
- R-squared

## Popular ML Libraries

### Python Ecosystem
- **scikit-learn**: General ML algorithms
- **TensorFlow**: Deep learning framework
- **PyTorch**: Deep learning and research
- **Pandas**: Data manipulation
- **NumPy**: Numerical computing

## Real-World Applications

1. **Healthcare**: Disease prediction, drug discovery
2. **Finance**: Credit scoring, algorithmic trading
3. **E-commerce**: Product recommendations
4. **Marketing**: Customer segmentation, churn prediction
5. **Manufacturing**: Predictive maintenance, quality control
6. **Transportation**: Route optimization, demand forecasting

## Best Practices

- Start with simple models
- Always validate on separate test data
- Monitor for bias and fairness
- Document your process
- Keep learning and experimenting

Machine Learning is revolutionizing how we solve problems. Understanding its fundamentals opens doors to countless applications and innovations.
  `,
  duration: '120 minutes',
  difficulty: 'intermediate',
  order: 3,
  estimatedTime: '120 minutes',
  objectives: [
    'Understand the core concepts of machine learning',
    'Differentiate between supervised, unsupervised, and reinforcement learning',
    'Identify common machine learning algorithms and their use cases',
    'Recognize the importance of data in machine learning'
  ],
  learningObjectives: [
    'Understand the core concepts of machine learning',
    'Differentiate between supervised, unsupervised, and reinforcement learning',
    'Identify common machine learning algorithms and their use cases',
    'Recognize the importance of data in machine learning'
  ],
  prerequisites: ['AI fundamentals', 'Basic statistics', 'Python programming'],
  practice: {
    task: `
## Your Challenge
Build a simple machine learning model to classify data using scikit-learn.

### Requirements
- Load and preprocess a dataset
- Train a classification model
- Evaluate model performance
- Make predictions on new data

### Success Criteria
- Model achieves good accuracy on test data
- Code is well-documented
- Results are properly interpreted
    `,
    starter: `# Machine Learning Classification Example

import numpy as np
from sklearn.datasets import load_iris
from sklearn.model_selection import train_test_split
from sklearn.tree import DecisionTreeClassifier
from sklearn.metrics import accuracy_score

# Load dataset
data = load_iris()
X, y = data.data, data.target

# TODO: Split data into training and testing sets
# TODO: Train a classification model
# TODO: Make predictions
# TODO: Evaluate performance`,
    hints: [
      'Use train_test_split to divide your data',
      'Consider starting with a Decision Tree classifier',
      'Calculate accuracy on both training and test sets',
      'Think about overfitting vs underfitting'
    ],
    estimatedTime: 90
  },
  quiz: {
    questions: [
      {
        id: 'ml-basics-q1',
        question: 'Which type of learning uses labeled training data?',
        options: [
          'Unsupervised Learning',
          'Supervised Learning',
          'Reinforcement Learning',
          'Transfer Learning'
        ],
        correctAnswer: 1,
        explanation: 'Supervised Learning uses labeled training data where each example is paired with the correct output, allowing the model to learn the mapping from inputs to outputs.'
      },
      {
        id: 'ml-basics-q2',
        question: 'What is overfitting in machine learning?',
        options: [
          'When the model is too simple',
          'When the model memorizes training data and performs poorly on new data',
          'When the model trains too quickly',
          'When the model has too few parameters'
        ],
        correctAnswer: 1,
        explanation: 'Overfitting occurs when a model is too complex and learns the training data too well, including noise and outliers, resulting in poor performance on new, unseen data.'
      }
    ]
  }
};
